/**
 * Landing Page WOW Components
 * Premium interactive elements for MiCoach landing page
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { 
  Play, Pause, ChevronRight, ChevronLeft, Calendar, Users, Target, 
  TrendingUp, Zap, Copy, BookOpen, BarChart3, Bell, Clock,
  ArrowRight, Shield, Trophy, Layers
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// 1. HERO INTERACTIVE DEMO - Real product demo with tabs
// ═══════════════════════════════════════════════════════════════════════════════
export function HeroInteractiveDemo() {
  const [activeTab, setActiveTab] = useState('microciclo');
  const tabs = [
    { id: 'microciclo', label: 'Microciclo', color: 'var(--primary)' },
    { id: 'pizarra', label: 'Pizarra', color: '#ffd34d' },
    { id: 'vestuario', label: 'Vestuario', color: '#D4AF37' },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ 
      background: '#141E28', 
      border: '1px solid #1A242D',
      boxShadow: '0 40px 80px rgba(0,0,0,0.55)'
    }}>
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid #1A242D' }}>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff4d6d' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ffd34d' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--primary)' }} />
        </div>
        <div className="flex-1 flex justify-center">
          <span className="text-[10px] px-3 py-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.03)' }}>
            app.micoach.tech
          </span>
        </div>
      </div>

      {/* Micro copy */}
      <div className="px-4 py-2 text-center" style={{ background: 'rgba(var(--primary-rgb),0.03)', borderBottom: '1px solid #1A242D' }}>
        <p className="text-[10px] font-bold" style={{ color: 'var(--primary)', letterSpacing: '0.1em' }}>
          TÓCALO. ASÍ SE TRABAJA DENTRO.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex px-2 pt-2 gap-1" style={{ borderBottom: '1px solid #1A242D' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="relative px-4 py-2 text-[11px] font-bold transition-all"
            style={{ 
              color: activeTab === tab.id ? tab.color : 'rgba(255,255,255,0.25)',
            }}
          >
            {tab.label}
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ background: tab.color }}
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4" style={{ minHeight: 260 }}>
        <AnimatePresence mode="wait">
          {activeTab === 'microciclo' && <MicrocicloDemo key="microciclo" />}
          {activeTab === 'pizarra' && <PizarraDemo key="pizarra" />}
          {activeTab === 'vestuario' && <VestuarioDemo key="vestuario" />}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Microciclo Demo - Interactive MD selector
function MicrocicloDemo() {
  const [selectedDay, setSelectedDay] = useState('MD-2');
  const days = [
    { id: 'MD-4', label: 'MD-4', load: 85, focus: 'Fuerza + Pressing', sessions: 2, color: '#ff4d6d' },
    { id: 'MD-3', label: 'MD-3', load: 70, focus: 'Táctica colectiva', sessions: 1, color: '#ffd34d' },
    { id: 'MD-2', label: 'MD-2', load: 55, focus: 'Finalización + ABP', sessions: 1, color: 'var(--primary)' },
    { id: 'MD-1', label: 'MD-1', load: 30, focus: 'Activación + Charla', sessions: 1, color: '#4dd6ff' },
  ];
  const current = days.find(d => d.id === selectedDay) || days[2];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      {/* Day selector */}
      <div className="flex gap-2 mb-4">
        {days.map(day => (
          <button
            key={day.id}
            onClick={() => setSelectedDay(day.id)}
            className="flex-1 py-2 px-2 rounded-lg text-center transition-all"
            style={{
              background: selectedDay === day.id ? `${day.color}15` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${selectedDay === day.id ? `${day.color}40` : '#1A242D'}`,
            }}
          >
            <p className="text-[10px] font-black" style={{ color: selectedDay === day.id ? day.color : 'rgba(255,255,255,0.3)' }}>
              {day.label}
            </p>
            <div className="h-1 rounded-full mt-1 mx-auto" style={{ 
              width: `${day.load}%`, 
              background: selectedDay === day.id ? day.color : 'rgba(255,255,255,0.08)' 
            }} />
          </button>
        ))}
      </div>

      {/* Day details */}
      <motion.div
        key={selectedDay}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="rounded-xl p-4"
        style={{ background: `${current.color}08`, border: `1px solid ${current.color}20` }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-black" style={{ fontFamily: 'Inter, sans-serif', color: current.color }}>
              {current.label}
            </p>
            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {current.sessions} sesión · {current.load}% carga
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${current.color}15` }}>
            <span className="text-xl font-black" style={{ color: current.color }}>{current.load}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Target className="w-3.5 h-3.5" style={{ color: current.color }} />
          <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>Foco: {current.focus}</span>
        </div>

        {/* Mini session preview */}
        <div className="space-y-1 mt-3">
          {['Activación', 'Rondo técnico', current.focus.split(' + ')[0]].map((task, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="w-1 h-4 rounded-full" style={{ background: current.color, opacity: 0.5 + i * 0.2 }} />
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{task}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Pizarra Demo - Interactive step-by-step board
function PizarraDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const steps = [
    { label: 'Posición inicial', positions: [[50,85],[20,65],[40,62],[60,62],[80,65],[30,45],[50,42],[70,45],[25,25],[50,20],[75,25]] },
    { label: 'Pressing alto', positions: [[50,80],[22,55],[42,50],[58,50],[78,55],[32,35],[50,32],[68,35],[28,18],[50,14],[72,18]] },
    { label: 'Ataque combinado', positions: [[50,78],[18,48],[38,45],[62,45],[82,48],[35,28],[50,24],[65,28],[22,12],[50,8],[78,12]] },
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(() => {
        setCurrentStep(prev => (prev + 1) % steps.length);
      }, 1500);
      return () => clearInterval(timer);
    }
  }, [isPlaying, steps.length]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      {/* Step indicators */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1">
          {steps.map((step, i) => (
            <button
              key={i}
              onClick={() => { setCurrentStep(i); setIsPlaying(false); }}
              className="px-3 py-1 rounded-lg text-[9px] font-bold transition-all"
              style={{
                background: currentStep === i ? 'rgba(255,215,77,0.15)' : 'rgba(255,255,255,0.03)',
                color: currentStep === i ? '#ffd34d' : 'rgba(255,255,255,0.25)',
                border: `1px solid ${currentStep === i ? 'rgba(255,215,77,0.3)' : 'transparent'}`,
              }}
            >
              Paso {i + 1}
            </button>
          ))}
        </div>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: isPlaying ? 'rgba(255,77,109,0.15)' : 'rgba(var(--primary-rgb),0.15)' }}
        >
          {isPlaying ? <Pause className="w-4 h-4" style={{ color: '#ff4d6d' }} /> : <Play className="w-4 h-4" style={{ color: 'var(--primary)' }} />}
        </button>
      </div>

      {/* Field */}
      <div className="relative rounded-xl overflow-hidden" style={{ 
        paddingBottom: '65%',
        background: 'linear-gradient(180deg, #1d8240 0%, #178035 50%, #1d8240 100%)'
      }}>
        {/* Field markings */}
        <div className="absolute inset-[4%] border border-white/20 rounded-sm" />
        <div className="absolute top-[4%] left-[20%] right-[20%] h-[18%] border border-white/15" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[15%] h-[22%] border border-white/15 rounded-full" />

        {/* Players */}
        {steps[currentStep].positions.map((pos, i) => (
          <motion.div
            key={i}
            className="absolute w-[6%] h-[9%] rounded-full flex items-center justify-center text-[8px] font-black"
            style={{
              background: 'var(--primary)',
              border: '2px solid rgba(255,255,255,0.8)',
              color: '#000',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
            animate={{
              left: `${pos[0] - 3}%`,
              top: `${pos[1] - 4.5}%`,
            }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            {i + 1}
          </motion.div>
        ))}

        {/* Ball */}
        <motion.div
          className="absolute w-[3.5%] h-[5%] rounded-full bg-white"
          style={{ boxShadow: '0 0 10px rgba(255,255,255,0.5)' }}
          animate={{
            left: `${[48, 44, 50][currentStep]}%`,
            top: `${[38, 28, 12][currentStep]}%`,
          }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        />

        {/* Step label */}
        <div className="absolute bottom-2 left-2 right-2 text-center">
          <span className="text-[10px] font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.6)', color: '#ffd34d' }}>
            {steps[currentStep].label}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// Vestuario Demo - Player state chips
function VestuarioDemo() {
  const [selectedPlayer, setSelectedPlayer] = useState(0);
  const players = [
    { name: 'Marco Alonso', pos: 'MC', confidence: 5, rhythm: 4, load: 3, role: 'Titular', focus: 'Mantener nivel' },
    { name: 'Hugo Ramos', pos: 'DC', confidence: 3, rhythm: 3, load: 4, role: 'Rotación', focus: 'Mejorar salida' },
    { name: 'Pablo Ruiz', pos: 'EI', confidence: 2, rhythm: 2, load: 5, role: 'Descanso', focus: 'Recuperar confianza' },
  ];
  const current = players[selectedPlayer];
  const chips = [
    { label: 'Confianza', value: current.confidence, color: current.confidence >= 4 ? 'var(--primary)' : current.confidence >= 3 ? '#ffd34d' : '#ff4d6d' },
    { label: 'Ritmo', value: current.rhythm, color: current.rhythm >= 4 ? 'var(--primary)' : current.rhythm >= 3 ? '#ffd34d' : '#ff4d6d' },
    { label: 'Carga', value: current.load, color: current.load <= 3 ? 'var(--primary)' : current.load <= 4 ? '#ffd34d' : '#ff4d6d' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
      {/* Player selector */}
      <div className="flex gap-2 mb-4">
        {players.map((player, i) => (
          <button
            key={i}
            onClick={() => setSelectedPlayer(i)}
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl transition-all"
            style={{
              background: selectedPlayer === i ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${selectedPlayer === i ? 'rgba(212,175,55,0.3)' : '#1A242D'}`,
            }}
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black"
              style={{ background: selectedPlayer === i ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.05)', color: selectedPlayer === i ? '#D4AF37' : 'rgba(255,255,255,0.3)' }}>
              {player.name[0]}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-[10px] font-bold" style={{ color: selectedPlayer === i ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)' }}>{player.name.split(' ')[0]}</p>
              <p className="text-[8px]" style={{ color: 'rgba(255,255,255,0.2)' }}>{player.pos}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Player card */}
      <motion.div
        key={selectedPlayer}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-xl p-4"
        style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-base font-black" style={{ fontFamily: 'Inter, sans-serif' }}>{current.name}</p>
            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{current.pos} · {current.role}</p>
          </div>
          <span className="text-[9px] font-bold px-2 py-1 rounded-full"
            style={{ background: current.role === 'Titular' ? 'rgba(var(--primary-rgb),0.15)' : current.role === 'Rotación' ? 'rgba(255,211,77,0.15)' : 'rgba(255,77,109,0.15)',
                     color: current.role === 'Titular' ? 'var(--primary)' : current.role === 'Rotación' ? '#ffd34d' : '#ff4d6d' }}>
            {current.role}
          </span>
        </div>

        {/* State chips */}
        <div className="flex gap-2 mb-3">
          {chips.map((chip, i) => (
            <div key={i} className="flex-1 rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p className="text-[8px] mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{chip.label}</p>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(v => (
                  <div key={v} className="w-2 h-2 rounded-full" style={{ background: v <= chip.value ? chip.color : 'rgba(255,255,255,0.08)' }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Focus */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <Target className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} />
          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)' }}>Foco: {current.focus}</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. BEFORE/AFTER SLIDER - Drag comparison
// ═══════════════════════════════════════════════════════════════════════════════
export function BeforeAfterSlider() {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  // Mueve el separador a la posición X del puntero (0–100%, llega hasta el final).
  const moveTo = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(percent);
  }, []);

  // Pointer Events: unifican ratón y táctil. setPointerCapture evita que el
  // navegador "pierda" el arrastre en escritorio (selección / drag nativo), que
  // era la causa de que no llegara hasta el final.
  const onPointerDown = useCallback((e) => {
    isDragging.current = true;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) { /* noop */ }
    moveTo(e.clientX);
  }, [moveTo]);

  const onPointerMove = useCallback((e) => {
    if (isDragging.current) moveTo(e.clientX);
  }, [moveTo]);

  const stopDrag = useCallback(() => { isDragging.current = false; }, []);

  return (
    <div
      ref={containerRef}
      className="relative rounded-2xl overflow-hidden select-none cursor-ew-resize"
      style={{ height: 380, background: '#141E28', border: '1px solid #1A242D', touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onPointerLeave={stopDrag}
    >
      {/* BEFORE - Left side (always full width, clipped) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
        <div className="h-full p-6" style={{ background: 'rgba(255,77,109,0.03)' }}>
          <p className="text-[10px] font-black mb-4" style={{ color: '#ff4d6d', letterSpacing: '0.1em' }}>ANTES</p>
          <div className="space-y-3">
            {[
              { icon: '📱', text: 'WhatsApp "Staff técnico"', sub: '47 mensajes sin leer' },
              { icon: '📄', text: 'sesion_martes_v3_FINAL.pdf', sub: 'Hace 2 semanas' },
              { icon: '📝', text: 'Notas del partido', sub: '¿Dónde las dejé?' },
              { icon: '❓', text: 'Convocatoria sábado', sub: 'Marc tocado... ¿o era Hugo?' },
              { icon: '🔄', text: 'Cada semana empiezas de cero', sub: '' },
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,77,109,0.1)' }}
              >
                <span className="text-lg">{item.icon}</span>
                <div>
                  <p className="text-[11px] font-bold" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.text}</p>
                  {item.sub && <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.25)' }}>{item.sub}</p>}
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-[10px] mt-4 font-bold" style={{ color: 'rgba(255,77,109,0.5)' }}>
            Disperso. Sin sistema. Improvisando.
          </p>
        </div>
      </div>

      {/* AFTER - Right side */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}>
        <div className="h-full p-6" style={{ background: 'rgba(var(--primary-rgb),0.03)' }}>
          <p className="text-[10px] font-black mb-4" style={{ color: 'var(--primary)', letterSpacing: '0.1em' }}>CON MICOACH</p>
          <div className="space-y-3">
            {[
              { icon: Calendar, text: 'Microciclo planificado', sub: 'MD-5 a MD+1 estructurado', color: 'var(--primary)' },
              { icon: Layers, text: 'Sesiones reutilizables', sub: 'Biblioteca personal', color: '#4dd6ff' },
              { icon: Users, text: 'Vestuario con datos', sub: 'Estado competitivo semanal', color: '#D4AF37' },
              { icon: Target, text: 'Decisiones coherentes', sub: 'Basadas en contexto real', color: '#a78bfa' },
              { icon: TrendingUp, text: 'Progresión medible', sub: 'Tu metodología crece', color: 'var(--primary)' },
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item.color}20` }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}15` }}>
                  <item.icon className="w-4 h-4" style={{ color: item.color }} />
                </div>
                <div>
                  <p className="text-[11px] font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>{item.text}</p>
                  <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{item.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-[10px] mt-4 font-bold" style={{ color: 'rgba(var(--primary-rgb),0.5)' }}>
            Estructurado. Medible. Profesional.
          </p>
        </div>
      </div>

      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 w-1 cursor-ew-resize z-10 pointer-events-none"
        style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)', background: 'rgba(255,255,255,0.3)' }}
      >
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: '#1A242D', border: '2px solid rgba(255,255,255,0.3)' }}
        >
          <ChevronLeft className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.5)' }} />
          <ChevronRight className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.5)' }} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. MATCH TO WEEK TIMELINE - Animated story
// ═══════════════════════════════════════════════════════════════════════════════
export function MatchToWeekTimeline() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const steps = [
    { icon: Trophy, title: 'Partido', desc: 'Registras resultado, notas y observaciones clave', color: '#ff4d6d' },
    { icon: Zap, title: 'Insight', desc: 'Se detecta el foco semanal basado en datos', color: '#ffd34d' },
    { icon: Calendar, title: 'Microciclo', desc: 'Objetivos + sesiones conectadas al foco', color: 'var(--primary)' },
    { icon: Users, title: 'Jugador', desc: 'Estado competitivo y plan individual', color: '#D4AF37' },
  ];

  return (
    <div ref={ref} className="relative">
      {/* Connecting line */}
      <div className="absolute top-12 left-0 right-0 h-0.5 hidden md:block" style={{ background: '#1A242D' }}>
        <motion.div
          className="h-full w-full origin-left"
          style={{ background: 'linear-gradient(90deg, #ff4d6d, #ffd34d, #10B981, #D4AF37)' }}
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: i * 0.2, duration: 0.5 }}
            className="relative"
          >
            {/* Node */}
            <div className="relative z-10 mb-4 flex justify-center">
              <motion.div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: `${step.color}15`, border: `2px solid ${step.color}30` }}
                whileHover={{ scale: 1.1, boxShadow: `0 0 30px ${step.color}30` }}
              >
                <step.icon className="w-7 h-7" style={{ color: step.color }} />
              </motion.div>
            </div>

            {/* Content */}
            <div className="text-center">
              <p className="text-sm font-black mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{step.title}</p>
              <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>{step.desc}</p>
            </div>

            {/* Step number */}
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black"
              style={{ background: step.color, color: '#000' }}>
              {i + 1}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. LIBRARY TEASER - Template cards with interaction
// ═══════════════════════════════════════════════════════════════════════════════
export function LibraryTeaser() {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const cards = [
    { title: 'Sesiones', count: '120+', icon: Calendar, color: 'var(--primary)', desc: 'Organizadas por objetivo' },
    { title: 'Pizarras', count: '45+', icon: Layers, color: '#ffd34d', desc: 'Con pasos animados' },
    { title: 'Microciclos', count: '30+', icon: BarChart3, color: '#4dd6ff', desc: 'Por fase de temporada' },
    { title: 'Planes individuales', count: '25+', icon: Target, color: '#D4AF37', desc: 'Por perfil de jugador' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <motion.div
          key={i}
          className="relative rounded-2xl p-5 cursor-pointer overflow-hidden"
          style={{ background: '#141E28', border: `1px solid ${hoveredCard === i ? `${card.color}40` : '#1A242D'}` }}
          onMouseEnter={() => setHoveredCard(i)}
          onMouseLeave={() => setHoveredCard(null)}
          whileHover={{ scale: 1.02, y: -4 }}
          transition={{ duration: 0.2 }}
        >
          {/* Icon */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
            style={{ background: `${card.color}12` }}>
            <card.icon className="w-5 h-5" style={{ color: card.color }} />
          </div>

          {/* Content */}
          <p className="text-base font-black mb-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{card.title}</p>
          <p className="text-lg font-black" style={{ color: card.color }}>{card.count}</p>
          <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{card.desc}</p>

          {/* Hover effect - duplicate icon */}
          <AnimatePresence>
            {hoveredCard === i && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: 20, y: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg"
                style={{ background: `${card.color}20` }}
              >
                <Copy className="w-3 h-3" style={{ color: card.color }} />
                <span className="text-[9px] font-bold" style={{ color: card.color }}>Guardar</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. CLUB MODE TEASER - Enterprise mini dashboard
// ═══════════════════════════════════════════════════════════════════════════════
export function ClubModeTeaser({ onRequestDemo }) {
  const stats = [
    { label: 'Equipos', value: '8', color: '#a78bfa' },
    { label: 'Jugadores', value: '124', color: 'var(--primary)' },
    { label: 'Alertas', value: '6', color: '#ff4d6d' },
    { label: 'Minutos', value: '2.4k', color: '#4dd6ff' },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#141E28', border: '1px solid rgba(167,139,250,0.2)' }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'rgba(167,139,250,0.05)', borderBottom: '1px solid rgba(167,139,250,0.1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(167,139,250,0.15)' }}>
            <Shield className="w-5 h-5" style={{ color: '#a78bfa' }} />
          </div>
          <div>
            <p className="text-sm font-black" style={{ fontFamily: 'Inter, sans-serif' }}>CLUB MODE</p>
            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>Para academias y clubes</p>
          </div>
        </div>
        <span className="text-[9px] font-bold px-2 py-1 rounded-full" style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa' }}>
          ENTERPRISE
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 p-4">
        {stats.map((stat, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="text-center p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)' }}
          >
            <p className="text-xl font-black" style={{ color: stat.color, fontFamily: 'Inter, sans-serif' }}>{stat.value}</p>
            <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Teams preview */}
      <div className="px-4 pb-2 space-y-1.5">
        {['Juvenil A · 18 jugadores · MD-2', 'Cadete A · 16 jugadores · MD-3', 'Infantil B · 14 jugadores · MD-1'].map((team, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <div className="w-1.5 h-5 rounded-full" style={{ background: '#a78bfa', opacity: 0.5 + i * 0.2 }} />
            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{team}</span>
            {i === 0 && <Bell className="w-3 h-3 ml-auto" style={{ color: '#ff4d6d' }} />}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="p-4">
        <motion.button
          onClick={onRequestDemo}
          className="w-full py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2"
          style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontFamily: 'Inter, sans-serif' }}
          whileHover={{ background: 'rgba(167,139,250,0.25)' }}
        >
          SOLICITAR DEMO CLUB <ArrowRight className="w-4 h-4" />
        </motion.button>
      </div>
    </div>
  );
}
